import { useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { TimelineItem } from '@/shared/api'
import { cn } from '@/shared/lib/cn'
import { guestDisplayName } from '@/shared/lib/format'
import { Icon, Skeleton, WidgetError } from '@/shared/ui'
import { conversationQuery, timelineQuery } from '../api/queries'
import { DaySeparator } from './DaySeparator'
import { MessageBubble } from './MessageBubble'
import { SystemEvent } from './SystemEvent'
import { SystemMessage } from './SystemMessage'
import { TechGroup } from './TechGroup'
import s from './ConversationThread.module.css'

type ThreadFilter = 'all' | 'dialog' | 'events'

const FILTERS: ReadonlyArray<{ value: ThreadFilter; label: string }> = [
  { value: 'all', label: 'Всё' },
  { value: 'dialog', label: 'Диалог' },
  { value: 'events', label: 'События' },
]

type ConversationThreadProps = {
  conversationId: string
  /**
   * Правый край верхней строки шапки. Двум потребителям ленты там нужно РАЗНОЕ: карточке брони —
   * кнопка «Открыть в мессенджере» (её тред рисует сам, если слот пуст), экрану «Чаты» — тумблер
   * «Менеджер / ИИ». Тумблер живёт в `features`, а `entities` не может импортировать `features` —
   * отсюда слот. Тот же приём, что `footer` у `features/company-switch/ui/CompanySwitcher`.
   */
  headerAction?: ReactNode
  /**
   * Ещё одна кнопка в шапке, ЛЕВЕЕ профиля. В отличие от `headerAction` слот не заменяет ничего, а
   * добавляет: карточке брони нужен «Экран гостя» ВМЕСТЕ с «Открыть в мессенджере», и уехав в
   * `headerAction`, он молча съел бы вторую кнопку.
   */
  headerExtra?: ReactNode
  /**
   * Хвост ленты — ВНУТРИ скролл-контейнера, последним элементом: очередь отправки и карточка
   * черновика ИИ. Оба — фичи, а `entities` их импортировать не может (FSD), отсюда слот.
   */
  feedEnd?: ReactNode
  /** Под лентой, вне скролла: композер (`features/message-send`). */
  footer?: ReactNode
  className?: string
}

/**
 * Лента переписки с гостем: шапка треда + фильтр (Всё / Диалог / События) + скроллящаяся
 * мультиканальная лента + слоты. Один компонент на ДВА места — панель карточки брони и экран
 * «Чаты»; всё, что у них разное, приходит слотами (см. `headerAction`).
 *
 * Данные — ДВА независимых запроса: шапка (`conversationQuery`) и лента (`timelineQuery`, поллинг).
 * Каждый показывает своё: **данные → ошибка → скелетон**, именно в таком порядке — упавший фоновый
 * перезапрос оставляет `data` и выставляет `error` (HANDOFF §5), и обратный порядок спрятал бы
 * живую переписку от вернувшегося во вкладку. Ошибку показываем, только если показать НЕЧЕГО.
 */
export function ConversationThread({
  conversationId,
  headerAction,
  headerExtra,
  feedEnd,
  footer,
  className,
}: ConversationThreadProps) {
  const {
    data: conversation,
    error: headError,
    refetch: refetchHead,
  } = useQuery(conversationQuery(conversationId))

  const {
    data: timeline,
    error: feedError,
    refetch: refetchFeed,
  } = useQuery(timelineQuery(conversationId))

  const [filter, setFilter] = useState<ThreadFilter>('all')

  const items = timeline?.items
  const visibleItems = useMemo(() => filterTimeline(items, filter), [items, filter])
  // «Ждёт ответа менеджера» приезжает в шапке (statusNote), но в макете стоит ВНИЗУ ленты. В
  // режиме «События» разговорной подписи не место — прячем вместе с диалогом.
  const statusNote = filter === 'dialog' || filter === 'all' ? conversation?.statusNote : null

  const feedRef = useRef<HTMLDivElement>(null)
  const atBottomRef = useRef(true)

  /*
   * Лента открывается прокрученной В КОНЕЦ и доскролливается на новые сообщения, только если
   * человек И ТАК был внизу (иначе поллинг раз в N секунд дёргал бы историю из-под курсора).
   * `useLayoutEffect` — до покраски. Зависимости: `visibleItems` (структурное разделение Query
   * держит ссылку между тиками без новостей), `statusNote`, `feedEnd` (растёт от очереди/textarea).
   */
  useLayoutEffect(() => {
    const feed = feedRef.current
    if (!feed || !atBottomRef.current) return
    feed.scrollTop = feed.scrollHeight
  }, [visibleItems, statusNote, feedEnd])

  const channelCount = conversation ? conversation.sendChannels.filter((c) => c.available).length : 0

  return (
    <div className={cn(s.root, className)}>
      <header className={s.head}>
        {conversation ? (
          <>
            <div className={s.headTop}>
              <span className={s.avatar} aria-hidden="true">
                {/* У безымянного гостя инициалов не из чего собрать — тот же приём, что в строке
                    базы гостей: вместо букв глиф канала (`pages/studio/ui/ClientList`). */}
                {conversation.guestInitials || <Icon name="phone" size={13} strokeWidth={1.8} />}
              </span>

              <div className={s.headText}>
                <div className={s.headName}>{guestDisplayName(conversation.guestName)}</div>
                <div className={s.headMeta}>
                  гость · {channelCount} {pluralChannels(channelCount)}
                </div>
              </div>

              {headerExtra}

              {/* Профиль гостя — только когда у него есть адрес; нет → кнопки нет вовсе. */}
              {conversation.guestProfileUrl ? (
                <a
                  className={s.profile}
                  href={conversation.guestProfileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Профиль
                </a>
              ) : null}

              {/*
               * Слот пуст (карточка брони) — рисуем «Открыть в мессенджере». Адрес даёт СЕРВЕР
               * (`externalUrl`); `null` → кнопки нет вовсе, мёртвых кнопок в проде не рисуем.
               */}
              {headerAction ??
                (conversation.externalUrl ? (
                  <a
                    className={s.external}
                    href={conversation.externalUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Открыть в мессенджере"
                    aria-label="Открыть в мессенджере"
                  >
                    <Icon name="external" size={15} strokeWidth={1.9} />
                  </a>
                ) : null)}
            </div>

            <div className={s.filter} role="tablist" aria-label="Фильтр ленты">
              {FILTERS.map((entry) => (
                <button
                  key={entry.value}
                  type="button"
                  role="tab"
                  className={s.filterTab}
                  aria-selected={filter === entry.value}
                  onClick={() => setFilter(entry.value)}
                >
                  {entry.label}
                </button>
              ))}
            </div>
          </>
        ) : headError ? (
          <div className={s.headError}>
            <WidgetError error={headError} retry={() => void refetchHead()} />
          </div>
        ) : (
          <div className={s.headTop}>
            <Skeleton width={36} height={36} radius={10} />
            <div className={s.headSkeleton}>
              <Skeleton width="54%" height={13} radius={4} />
              <Skeleton width="76%" height={11} radius={4} />
            </div>
          </div>
        )}
      </header>

      <div
        className={s.feed}
        ref={feedRef}
        onScroll={() => {
          const feed = feedRef.current
          if (!feed) return
          atBottomRef.current = feed.scrollHeight - feed.scrollTop - feed.clientHeight <= BOTTOM_SLACK
        }}
      >
        {items ? (
          <>
            {visibleItems.map(renderItem)}

            {visibleItems.length === 0 ? (
              <p className={s.filterEmpty}>В этом фильтре пока пусто.</p>
            ) : null}

            {statusNote ? (
              <div className={s.statusNote}>
                <span className={s.statusDot} aria-hidden="true" />
                {statusNote}
              </div>
            ) : null}
          </>
        ) : feedError ? (
          <WidgetError error={feedError} retry={() => void refetchFeed()} />
        ) : (
          <div className={s.feedSkeleton}>
            <Skeleton className={s.skeletonIn} width="64%" height={52} radius={14} />
            <Skeleton className={s.skeletonOut} width="76%" height={66} radius={14} />
            <Skeleton className={s.skeletonIn} width="48%" height={38} radius={14} />
          </div>
        )}

        {/* Хвост ленты — ВНЕ ветки «данные → ошибка → скелетон»: неотправленное сообщение живёт не
            в ленте, а в попытках отправки, и обязано остаться на экране, даже если лента не
            загрузилась. Фильтр «События» его прячет — очередь отправки это диалог. */}
        {filter === 'events' ? null : feedEnd}
      </div>

      {footer}
    </div>
  )
}

const BOTTOM_SLACK = 32

/**
 * Клиентский фильтр ленты (контракта не трогает). `dialog` — только сообщения, `events` — всё
 * прочее (события брони, системные записи, тех-группы). Разделители дней остаются, но осиротевшие
 * после фильтрации (день, за которым до следующего дня или конца ленты уже ничего нет) убираем —
 * иначе в шапке дня висела бы пустота.
 */
function filterTimeline(items: ReadonlyArray<TimelineItem> | undefined, filter: ThreadFilter): TimelineItem[] {
  if (!items) return []
  if (filter === 'all') return [...items]

  const kept = items.filter((item) => {
    if (item.type === 'day') return true
    return filter === 'dialog' ? item.type === 'message' : item.type !== 'message'
  })

  return kept.filter((item, index) => {
    if (item.type !== 'day') return true
    const next = kept[index + 1]
    return next !== undefined && next.type !== 'day'
  })
}

/** Русская словоформа: 1 канал, 2–4 канала, 5+ каналов. */
function pluralChannels(n: number): string {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return 'канал связи'
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'канала связи'
  return 'каналов связи'
}

/**
 * `TimelineItem` — дискриминированное объединение контракта, разбирается по `type`. Незнакомый
 * вид пропускаем, а не роняем всю переписку — как и в картах `model/`.
 */
function renderItem(item: TimelineItem) {
  switch (item.type) {
    case 'day':
      return <DaySeparator key={item.id} label={item.label} />
    case 'message':
      return <MessageBubble key={item.id} message={item} />
    case 'event':
      return <SystemEvent key={item.id} event={item} />
    case 'system':
      return <SystemMessage key={item.id} item={item} />
    case 'tech':
      return <TechGroup key={item.id} item={item} />
    default:
      return null
  }
}
